import styled from 'styled-components';

import { tokens, media } from '../../../design/tokens';

/**
 * Âncora das setas. Não recorta nada — quem recorta é o Track.
 */
export const Viewport = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * O trilho da fileira.
 *
 * Substitui o track anterior, que era um flex com `marginLeft` inline
 * animado por estado React, com a largura de card chumbada em 280 na
 * aritmética das setas e medição feita uma única vez (nunca em resize).
 *
 * O PROBLEMA CENTRAL: `overflow-x: auto` é necessário para rolar, mas
 * recorta o card que cresce no hover. A saída é que `overflow` recorta no
 * PADDING BOX, não no content box — então o padding vertical vira área de
 * pintura disponível DENTRO do retângulo de recorte, e a margem negativa de
 * mesmo valor devolve a altura original para o fluxo, sem empurrar a fileira
 * de baixo.
 *
 * A invariante que mantém isso correto está comentada em design/tokens.ts,
 * no bloco `row`. Mexer em `card.hoverScale` exige refazer a conta.
 *
 * O recorte HORIZONTAL nas pontas sai de graça pela geometria: com
 * `scroll-padding` de 5%, o card extremo repousa 5% para dentro da borda de
 * recorte depois de qualquer rolagem — em 1440px são 72px de folga para um
 * card que cresce ~13px por lado.
 */
export const Track = styled.ul`
  /* Quantas capas cabem por vista, com uma lasca da próxima sempre visível
     na borda direita: é assim que a fileira diz "tem mais coisa aqui". */
  --per-view: 2;
  --peek: 0.3;
  --gap: ${tokens.card.gap};

  ${media.from(tokens.breakpoint.xs)} {
    --per-view: 3;
    --peek: 0.25;
  }

  ${media.from(tokens.breakpoint.sm)} {
    --per-view: 4;
    --peek: 0.22;
  }

  ${media.from(tokens.breakpoint.md)} {
    --per-view: 4;
    --peek: 0.2;
  }

  ${media.from(tokens.breakpoint.lg)} {
    --per-view: 5;
    --peek: 0.18;
  }

  ${media.from(tokens.breakpoint.xl)} {
    --per-view: 6;
    --peek: 0.16;
  }

  ${media.from(tokens.breakpoint.xxl)} {
    --per-view: 7;
    --peek: 0.16;
  }

  --card-w: clamp(
    ${tokens.card.minWidth},
    calc(
      (100% - (var(--per-view) + var(--peek) - 1) * var(--gap)) /
        (var(--per-view) + var(--peek))
    ),
    ${tokens.card.maxWidth}
  );

  display: flex;
  /* Um card mais alto nunca estica os irmãos. */
  align-items: flex-start;
  list-style: none;
  margin: 0;

  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;

  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;

  /* A folga de pintura do card em hover — ver o comentário acima. */
  padding-top: ${tokens.row.bleedTop};
  padding-bottom: ${tokens.row.bleedBottom};
  margin-top: -${tokens.row.bleedTop};
  margin-bottom: -${tokens.row.bleedBottom};

  padding-left: ${tokens.space.gutter};
  padding-right: ${tokens.space.gutter};
  scroll-padding-left: ${tokens.space.gutter};
  scroll-padding-right: ${tokens.space.gutter};

  /* A regra global de esconder scrollbar só cobre webkit. */
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    scroll-behavior: auto;
  }
`;

export const Item = styled.li`
  flex: 0 0 var(--card-w);
  max-width: var(--card-w);

  /* A propriedade gap em flex só existe a partir do Chrome 84, e o piso
     deste projeto é Android antigo (ver PRODUCT.md). */
  margin-right: var(--gap);

  scroll-snap-align: start;
  scroll-margin-left: ${tokens.space.gutter};
  scroll-margin-right: ${tokens.space.gutter};
  /* O header tem 88px e é fixed na home: sem isto, o card focado por teclado
     para debaixo dele. */
  scroll-margin-top: 100px;

  &:last-child {
    margin-right: 0;
  }
`;

/**
 * Seta de rolagem.
 *
 * O fundo é um gradiente do próprio Preto de Palco — a fileira se dissolve
 * sob o controle, em vez do círculo preto flutuante de antes. Nenhuma cor
 * nova entra.
 */
export const Arrow = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${(props) => (props.$side === 'left' ? 'left: 0;' : 'right: 0;')}
  width: calc(${tokens.space.gutter} + 16px);
  z-index: ${tokens.zIndex.rowArrow};

  display: flex;
  align-items: center;
  ${(props) =>
    props.$side === 'left' ? 'justify-content: flex-start;' : 'justify-content: flex-end;'}
  padding: 0 ${tokens.space.xs};

  border: 0;
  cursor: pointer;
  color: ${tokens.color.textWhite};

  background: linear-gradient(
    to ${(props) => (props.$side === 'left' ? 'right' : 'left')},
    rgba(15, 15, 15, 0.92) 0%,
    rgba(15, 15, 15, 0) 100%
  );

  opacity: 0;
  transition: opacity ${tokens.motion.quick} ease;

  > svg {
    font-size: 1.75rem;
  }

  /* O mouse só vê a seta ao entrar na fileira; o teclado vê sempre que algo
     dentro da fileira estiver focado. */
  ${Viewport}:hover &,
  ${Viewport}:focus-within &,
  &:focus-visible {
    opacity: 1;
  }

  &:disabled {
    opacity: 0 !important;
    pointer-events: none;
  }

  /* No toque o scroll nativo já resolve, e a seta só rouba área da capa. */
  @media (hover: none) {
    display: none;
  }
`;
